@use '@/sass/variables';

// Shared node dimensions so the card and its action button stay in lockstep (no design-system token).
$node-width: 16rem;
$node-height: 2rem;

// --- ReadOnlyNode ----------------------------------------------------------

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: variables.$spacing-extra-small;
  width: $node-width;
  min-height: $node-height;
  padding: variables.$spacing-small variables.$spacing-medium;
  background: variables.$white;
  border: 1px solid variables.$gray;
  border-radius: variables.$border-radius-2;
  box-shadow: 0 1px 2px variables.$black-10;
}

// Status-toned card outlines — the node border echoes its badge tone (e.g. red on a failed session).
.cardError {
  border-color: variables.$red;
}

.cardSuccess {
  border-color: variables.$green;
}

.label {
  font-size: variables.$font-size-8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: variables.$dark-gray;
}

.title {
  font-size: variables.$font-size-6;
  color: variables.$black;
}

.badge {
  position: absolute;
  top: variables.$spacing-small;
  right: variables.$spacing-small;
  width: 1rem;
  height: 1rem;
}

// Continuous rotation for an in-progress (loader) badge icon.
.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// Global `.icon` sets `fill: currentColor`, so drive badge colour via `color`, not `fill`.
.toneSuccess {
  color: variables.$green;
}

.toneError {
  color: variables.$red;
}

.toneNeutral {
  color: variables.$dark-gray;
}

// --- ButtonNode ------------------------------------------------------------

.buttonWrapper {
  position: relative;
  width: $node-width;
}

.button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: $node-height;
  padding: variables.$spacing-small;
  background: variables.$blue;
  color: variables.$white;
  border: none;
  border-radius: variables.$border-radius-2;
  // Native <button> defaults to the OS UI font; inherit the app font so it matches the node text.
  font-family: inherit;
  font-size: variables.$font-size-6;
  line-height: 1;
  // Needed for the anchor branch.
  text-decoration: none;
  cursor: pointer;
  // The read-only canvas (nodesDraggable/elementsSelectable/nodesConnectable all false) makes React
  // Flow mark each node non-interactive with an inline `pointer-events: none`, which the button
  // inherits — so clicks fall through to the pane. Re-enable it here so the action stays clickable.
  pointer-events: auto;

  // The global `button:focus` rule (Button.scss) out-specifies this class and paints a dark pressed
  // fill; modals hand focus back to the node on close, so without this it looks stuck hovered.
  &:focus {
    background: variables.$blue;
    border: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid variables.$blue;
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    background: variables.$dark-blue;
  }

  &:disabled {
    cursor: default;
  }
}

// --- ActionCardNode --------------------------------------------------------

.actionCardWrapper {
  position: relative;
  width: $node-width;
}

// A lighter, secondary action affordance: a bordered white card (echoing ReadOnlyNode) rather than a
// solid dark button, so it reads as "click to configure" instead of a terminal run/download action.
.actionCard {
  display: flex;
  align-items: center;
  gap: variables.$spacing-small;
  width: 100%;
  min-height: $node-height;
  padding: variables.$spacing-small variables.$spacing-medium;
  background: variables.$white;
  color: variables.$black;
  border: 1px solid variables.$gray;
  border-radius: variables.$border-radius-2;
  box-shadow: 0 1px 2px variables.$black-10;
  // Native <button> defaults to the OS UI font; inherit the app font so it matches the node text.
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  // See ButtonNode note: the read-only canvas marks nodes non-interactive with inline
  // `pointer-events: none`; re-enable it here so the card stays clickable.
  pointer-events: auto;

  // See ButtonNode note: reset the global `button:focus` pressed fill so the card doesn't stay
  // dark blue once its modal returns focus to it.
  &:focus {
    background: variables.$white;
    border-color: variables.$gray;
    box-shadow: 0 1px 2px variables.$black-10;
  }

  &:focus-visible {
    outline: 2px solid variables.$blue;
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    border-color: variables.$blue;
    background: variables.$washed-blue;
  }

  &:disabled {
    cursor: default;
  }
}

.actionCardText {
  display: flex;
  flex-direction: column;
  gap: variables.$spacing-extra-small;
  flex: 1 1 auto;
  min-width: 0;
}

.actionCardTitle {
  font-size: variables.$font-size-6;
  font-weight: 600;
  color: variables.$black;
}

.actionCardSubtitle {
  font-size: variables.$font-size-8;
  color: variables.$dark-gray;
}

.actionCardChevron {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  color: variables.$dark-gray;
}

// --- Shared ----------------------------------------------------------------

// Hide the handle dot on a read-only graph, but collapse it flush to the node edge (min-width/height
// 0, no border/background) so the connecting edge meets the node border instead of the default dot,
// which sits offset outside the node and leaves a visible gap.
// Composed with React Flow's own `.react-flow__handle` class to win specificity regardless of the
// order the two stylesheets load (equal single-class specificity otherwise lets its dot styling win).
.handle:global(.react-flow__handle) {
  width: 1px;
  height: 1px;
  min-width: 0;
  min-height: 0;
  border: none;
  background: transparent;
  pointer-events: none;
}
